import { io, type Socket } from 'socket.io-client';
import { ApiError } from './api';
import { SESSION_PROTOCOL_VERSION } from './session-core';

const sayItKey = 'utage.activeSayItToken';
export type SayItStatus = 'awaiting_consent' | 'accepted' | 'submitted' | 'declined' | 'cancelled' | 'expired';
export type SayItReaction = '❤️' | '🥹' | '😂' | '🤝';
export interface SayItSnapshot {
  protocolVersion: 1;
  interaction: {
    id: string; status: SayItStatus; revision: number;
    sender: { userId: string; firstName: string };
    recipient: { userId: string; firstName: string };
    prompt: { key: string; text: string };
    responseText: string | null;
    reaction: SayItReaction | null;
    expiresAt: string;
  };
  viewerRole: 'sender' | 'recipient';
}
export interface SayItShare { deepLink: string; fallbackShareUrl: string; preparedMessageId: string | null; }
export interface SayItCreateResult {
  token: string; snapshot: SayItSnapshot;
  delivery: { sent: boolean; deepLink: string };
  share: SayItShare | null;
}

export const rememberedSayItToken = (): string | null => localStorage.getItem(sayItKey);
export const rememberSayItToken = (token: string | null): void => {
  if (token) localStorage.setItem(sayItKey, token); else localStorage.removeItem(sayItKey);
};

const request = async <T>(path: string, initData: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...options, cache: 'no-store',
    headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION), ...options?.headers }
  });
  const body = await response.json().catch(() => ({})) as T & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? 'Could not update this SAY IT moment.', body.error?.code);
  return body;
};

export const createSayIt = (initData: string, connectedUserId: string, actionId: string = crypto.randomUUID()): Promise<SayItCreateResult> =>
  request('/say-it', initData, { method: 'POST', body: JSON.stringify({ actionId, connectedUserId }) });
export const loadSayIt = async (token: string, initData: string): Promise<SayItSnapshot> =>
  (await request<{ snapshot: SayItSnapshot }>(`/say-it/${encodeURIComponent(token)}`, initData)).snapshot;
export const acceptSayIt = async (token: string, initData: string, actionId: string = crypto.randomUUID()): Promise<SayItSnapshot> =>
  (await request<{ snapshot: SayItSnapshot }>(`/say-it/${encodeURIComponent(token)}/accept`, initData, { method: 'POST', body: JSON.stringify({ actionId }) })).snapshot;
export const declineSayIt = async (token: string, initData: string, actionId: string = crypto.randomUUID()): Promise<SayItSnapshot> =>
  (await request<{ snapshot: SayItSnapshot }>(`/say-it/${encodeURIComponent(token)}/decline`, initData, { method: 'POST', body: JSON.stringify({ actionId }) })).snapshot;
export const cancelSayIt = async (token: string, initData: string, actionId: string = crypto.randomUUID()): Promise<SayItSnapshot> =>
  (await request<{ snapshot: SayItSnapshot }>(`/say-it/${encodeURIComponent(token)}/cancel`, initData, { method: 'POST', body: JSON.stringify({ actionId }) })).snapshot;
export const submitSayIt = async (token: string, initData: string, responseText: string, actionId: string = crypto.randomUUID()): Promise<SayItSnapshot> =>
  (await request<{ snapshot: SayItSnapshot }>(`/say-it/${encodeURIComponent(token)}/submit`, initData, { method: 'POST', body: JSON.stringify({ actionId, responseText }) })).snapshot;
export const reactSayIt = async (token: string, initData: string, reaction: SayItReaction, actionId: string = crypto.randomUUID()): Promise<SayItSnapshot> =>
  (await request<{ snapshot: SayItSnapshot }>(`/say-it/${encodeURIComponent(token)}/react`, initData, { method: 'POST', body: JSON.stringify({ actionId, reaction }) })).snapshot;

export const watchSayIt = (input: { token: string; initData: string; onSnapshot: (snapshot: SayItSnapshot) => void; onConnection: (connected: boolean) => void }): (() => void) => {
  const socket: Socket = io({ auth: { initData: input.initData, protocolVersion: SESSION_PROTOCOL_VERSION } });
  const webApp = window.Telegram?.WebApp;
  let disposed = false;
  let retryTimer: number | null = null;
  let restoreInFlight: Promise<void> | null = null;
  let lastRestoreAt = 0;
  const clearRetry = () => { if (retryTimer !== null) window.clearTimeout(retryTimer); retryTimer = null; };
  const restore = (): void => {
    if (disposed || restoreInFlight || Date.now() - lastRestoreAt < 250) return;
    lastRestoreAt = Date.now();
    restoreInFlight = loadSayIt(input.token, input.initData).then((snapshot) => { if (!disposed) input.onSnapshot(snapshot); }).catch(() => undefined).finally(() => { restoreInFlight = null; });
  };
  const subscribe = (): void => {
    if (disposed || !socket.connected) return;
    clearRetry();
    socket.emit('say-it:watch', { token: input.token }, (result: { ok: boolean; snapshot?: SayItSnapshot }) => {
      if (disposed) return;
      if (result.ok && result.snapshot) { input.onSnapshot(result.snapshot); input.onConnection(true); return; }
      input.onConnection(false); retryTimer = window.setTimeout(subscribe, 1_000);
    });
  };
  socket.on('connect', () => { input.onConnection(false); subscribe(); });
  socket.on('disconnect', () => { if (!disposed) input.onConnection(false); });
  socket.on('say-it:changed', restore);
  const visible = () => { if (document.visibilityState === 'visible') { restore(); subscribe(); } };
  const active = () => { restore(); subscribe(); };
  document.addEventListener('visibilitychange', visible);
  window.addEventListener('focus', active); window.addEventListener('pageshow', active); window.addEventListener('online', active);
  webApp?.onEvent('activated', active); webApp?.onEvent('shareMessageSent', active); webApp?.onEvent('shareMessageFailed', active);
  return () => {
    disposed = true; clearRetry(); socket.disconnect(); document.removeEventListener('visibilitychange', visible);
    window.removeEventListener('focus', active); window.removeEventListener('pageshow', active); window.removeEventListener('online', active);
    webApp?.offEvent('activated', active); webApp?.offEvent('shareMessageSent', active); webApp?.offEvent('shareMessageFailed', active);
  };
};

export const openSayItShare = (share: SayItShare): void => {
  const webApp = window.Telegram?.WebApp;
  if (share.preparedMessageId && webApp?.shareMessage) webApp.shareMessage(share.preparedMessageId);
  else if (webApp?.openTelegramLink) webApp.openTelegramLink(share.fallbackShareUrl);
  else window.open(share.fallbackShareUrl, '_blank', 'noopener,noreferrer');
};
